// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { SohoBusyIndicatorDirective } from 'ids-enterprise-ng';

import { ListResource } from '@resources/list.resource';
import { AppConstants, LMDialogButtonKey, LMGlobalUtilService, LMUniqueIdService } from '@utils/index';
import {
  LMContextService,
  LMFormatUtilService,
  LMLoggerService,
  LMModalDialogWrapperRef,
  LMModalOpenService,
  LMModalWrapperService,
  LMUrlUtilService
} from '@services/index';
import { LMErrorUtilService } from '@services/index';
import { LMScheduleDialogService } from './schedule/lm-schedule-dialog.service';
import { LMPrintCsvDialogComponent } from './csv/lm-print-csv-dialog.component';
import { LMPrintPdfDialogComponent } from './pdf/lm-print-pdf-dialog.component';
import { LMPrintRequestService } from './lm-print-request.service';
import { LMPollAndPrintDialogComponent } from '@modal/print-dialog/lm-poll-and-print-dialog.component';
import { DocumentResource } from '@app/resources';
import { LMGlobalPersonalizationService } from '@app/states';
import { LMReportsCatalogService } from '@app/components';
import { LMSecurityUtilService } from '@app/utils/lm-security-util.service';

@Injectable()
export class LMPrintDialogService {
  constructor(
    private lmLoggerService: LMLoggerService,
    private lmPrintRequestService: LMPrintRequestService,
    private lmScheduleDialogService: LMScheduleDialogService,
    private lmUniqueIdService: LMUniqueIdService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmContextService: LMContextService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmReportCatalogService: LMReportsCatalogService,
    private lmModalWrapperService: LMModalWrapperService,
    private lmModalOpenService: LMModalOpenService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmSecurityUtilService: LMSecurityUtilService
  ) {}

  private dialog: LMModalDialogWrapperRef<LMPrintDialog> = null;
  private pollDialog: LMModalDialogWrapperRef<LMPollAndPrintDialogComponent> = null;
  private component: LMPrintDialog;
  private printingMessage: string;
  private neverPoll: boolean;

  readonly printFilesResource = new ListResource({
    resourceId: 'UserReport',
    listName: 'MyUserReportsList'
  });

  private runForeground() {
    // Generate the report instead of scheduling and generating one using async.
    // This is available only in developer mode for debugging purpose.
    if (this.lmContextService.getContext().developer) {
      if (!this.component.checkIfFormIsValid()) {
        return;
      }
      const requestData = this.component.requestData;
      requestData.requestType = this.component.printInfo.isExport ? 'foregroundCsv' : 'foregroundPdf';
      const params = this.component.requestParams;
      const mimeType = (requestData.requestType === 'foregroundPdf') ? 'application/pdf'
        : ((params as LMCsvPrintParams).csvFormat === 1) ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      this.component.busyIndicator.activated = true;
      const options = {
        resource: this.component.resource,
        params,
        requestData
      } as LMRunPrintOptions;
      this.lmPrintRequestService.runPrintForeground(options).subscribe(
        (data: Blob) => {
          this.component.busyIndicator.activated = false;
          this.dialog.close();

          const blob = new Blob([ data ], { type: mimeType });
          const fileUrl = URL.createObjectURL(blob);
          const newTab = window.open(fileUrl);
          if (newTab) { // Check for popup blocker or csv downloaded
            if (requestData.requestType === 'foregroundPdf') {
              newTab.addEventListener('load', () => { URL.revokeObjectURL(fileUrl); });
            } else {
              // When downloading csv, window.onload is not fired.
              // Browser extensions downloads has patchy support across different browsers.
              setTimeout(() => { URL.revokeObjectURL(fileUrl); }, 5000);
            }
          }
        }
      );
    }
  }

  private runSchedule() {
    if (!this.component.checkIfFormIsValid()) {
      return;
    }
    const data = this.component.requestData;
    const name = data.scheduleView?.fields['Name']?.value;
    const params = this.component.requestParams;

    this.lmScheduleDialogService.openScheduleDialog(undefined, params.name)
      .subscribe((scheduleView: LMDataView) => {
        if (scheduleView) {
          // overwrite scheduleView of requestData
          data.scheduleView = scheduleView;
          data.scheduleView.fields['Name'].value = name;
          this.component.busyIndicator.activated = true;
          const options = {
            resource: this.component.resource,
            params,
            requestData: data
          } as LMRunPrintOptions;

          this.lmPrintRequestService.runPrint(options)
            .subscribe({
              next: (result: LMPrintResponse) => {
                this.component.busyIndicator.activated = false;
                const success = (result.status === AppConstants.STATUS_COMPLETED);
                if (success) {
                  this.dialog.close();
                  this.showPrintStatus(success, true, params);
                } else {
                  // LMWEB-23165: Added to handle malicious input and other result status other than complete
                  this.lmErrorUtilService.handleError(result.message, result.details, result.status);
                }
              },
              error: (err) => {
                this.component.busyIndicator.activated = false;
                this.lmErrorUtilService.handleUnexpectedError(err);
              }
            });
        }
      });
  }

  private runPrint(data?: LMPrintRequestData) {
    if (!this.component.checkIfFormIsValid()) {
      return;
    }
    const requestData = data ? data : this.component.requestData;
    const params = this.component.requestParams;

    /// Do not poll if numberOfThreads is > 1
    if ((params.numberOfThreads !== undefined && params.numberOfThreads > 1)
    || params.emptyReportOptions === AppConstants.SUPPRESS_WITH_NOTIFICATION || params.emptyReportOptions === AppConstants.SUPPRESS_WITHOUT_NOTIFICATION) {
      this.neverPoll = true;
    }

    this.component.busyIndicator.activated = true;
    const options = {
      resource: this.component.resource,
      params,
      requestData
    } as LMRunPrintOptions;
    this.lmPrintRequestService.runPrint(options)
      .subscribe({
        next: (result: LMPrintResponse) => {
          this.component.busyIndicator.activated = false;
          const success = (result.status === AppConstants.STATUS_COMPLETED);
          if (success) {
            this.dialog.close();
            if (!this.neverPoll) {
              this.lmLoggerService.log([ 'Begin poll for PDF/CSV generation.' ]);
              this.openPollDialog(result.asyncId, params.name);
            } else {
              this.showPrintStatus(success, !!data, params);
            }
          } else {
            // LMWEB-23165: Added to handle malicious input and other result status other than complete
            this.lmErrorUtilService.handleError(result.message, result.details, result.status);
          }
        },
        error: (err) => {
          this.component.busyIndicator.activated = false;
          this.lmErrorUtilService.handleUnexpectedError(err);
        }
      });
  }

  private showPrintStatus(success: boolean, scheduled: boolean, params: LMPrintRequestParams) {
    let url;
    if (this.lmContextService.getContext().enableReportCatalog) {
      url = this.lmReportCatalogService.buildCatalogNavigationUrl({ selectedPanel: 'printfiles' });
    } else {
      url = this.lmUrlUtilService.buildUrl({ resource: this.printFilesResource });
    }


    const toastAttributes: SohoAttributesArray = [];
    const toastMessageResult = success ? AppConstants.TRA_ACTION_COMPLETED : AppConstants.TRA_ACTION_FAILED;
    toastAttributes.push({ name: 'data-automation-id', value: 'lm-toast-message-container' });
    toastAttributes.push({ name: 'toast-message-result', value: toastMessageResult });

    const toastOptions = success ?
      this.component.getSuccessMessage(scheduled, params) :
      this.component.getErrorMessage(scheduled);

    toastOptions.attributes = toastAttributes;
    if (success && params.emptyReportOptions === 'default') {
      // Add link to 'My Print Files' for success messages
      toastOptions.allowLink = success;
      toastOptions.message =
        `<a class="hyperlink" target="_blank" href="${url}">
          ${toastOptions.message}</a>`;
    }

    this.lmGlobalUtilService.displayToastMessage(toastOptions.title, toastOptions.message, toastMessageResult, toastOptions.allowLink);
  }

  public getModalButtons(scheduleActionAllowed: boolean, isExport = false): SohoModalButton[] {
    const dialogId = 'PrintDialog';
    const printBtnText = isExport ? LMDialogButtonKey.EXPORT : LMDialogButtonKey.PRINT;
    const buttons: SohoModalButton[] = [
      {
        id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CANCEL ]),
        text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
        click: () => this.dialog.close()
      },
      {
        id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.SCHEDULE ]),
        text: Soho.Locale.translate(LMDialogButtonKey.SCHEDULE),
        click: () => this.runSchedule()
      },
      {
        id: this.lmUniqueIdService.uniqueId([ dialogId, printBtnText ]),
        text: Soho.Locale.translate(printBtnText),
        click: () => this.runPrint(),
        isDefault: true,
        validate : false
      },
    ];

    // Remove schedule button if not allowed
    if (!scheduleActionAllowed) {
      buttons.splice(1, 1);
    }

    if (this.lmContextService.getContext().developer) {
      const foregroundBtn =
        {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.FOREGROUND ]),
          // Hardcoded, since this is a developer only feature
          text: 'Foreground', // Soho.Locale.translate(LMDialogButtonKey.FOREGROUND),
          click: () => this.runForeground()
        };
      buttons.splice(1, 0, foregroundBtn);
    }
    return buttons;
  }

  private openDialog(info: LMPrintDialogInfo): LMModalDialogWrapperRef<LMPrintDialog> {
    this.component = this.dialog.componentDialog;
    this.component.printInfo = info;

    return this.dialog.title(info.actionReference.label || info.actionReference.tooltip)
      .buttons(this.getModalButtons(info.scheduleActionAllowed, info.isExport))
      .open();
  }

  /**
   * Open a PDF Print Dialog
   * @param info  dialog's settings
   */
  public openPdfDialog(info: LMPrintPdfInfo) {
    const attributes: SohoAttributesArray = [ { name:  'data-automation-id',  value: 'lm-print-to-pdf-modal' } ];
    const options: SohoModalOptions = {
      cssClass: 'lm-action-form-dialog lm-has-composite lm-export-tabbed-action-form',
      fullsize: 'always',
      attributes
    };

    // Initialize pdf modal
    const printing = Soho.Locale.translate('PrintingToPDF');
    this.printingMessage = this.lmFormatUtilService.formatMessage(printing, info.name);
    // If Never wait is set in User settings, maxWait will be 0.
    const maxWaitInSecs = this.lmGlobalPersonalizationService.getGlobalPersonalization().maxWaitPDFCSV as number;
    this.neverPoll = (maxWaitInSecs !== undefined && maxWaitInSecs === 0);
    this.dialog = this.lmModalWrapperService.modal(LMPrintPdfDialogComponent, options);
    return this.openDialog(info);
  }

  /**
   * Open a CSV Print Dialog
   * @param info  dialog's settings
   */
  public openCsvDialog(info: LMPrintCsvInfo) {
    // Initialize csv modal
    info.isExport = true;
    const printing = Soho.Locale.translate('CreatingCSVFile');
    this.printingMessage = this.lmFormatUtilService.formatMessage(printing, info.name);
    // If Never wait is set in User settings, maxWait will be 0.
    const maxWaitInSecs = this.lmGlobalPersonalizationService.getGlobalPersonalization().maxWaitPDFCSV as number;
    this.neverPoll = (maxWaitInSecs !== undefined && maxWaitInSecs === 0);
    const attributes: SohoAttributesArray = [ { name:  'data-automation-id',  value: 'lm-export-to-csv-modal' } ];
    const options: SohoModalOptions = {
      cssClass: 'lm-action-form-dialog lm-has-composite lm-export-tabbed-action-form',
      fullsize: 'always',
      attributes
    };
    this.dialog = this.lmModalWrapperService.modal(LMPrintCsvDialogComponent, options);
    return this.openDialog(info);
  }

  private openPollDialog(asyncId: string, name = ''): LMModalDialogWrapperRef<LMPollAndPrintDialogComponent>{
    const dialogId = 'PollDialog';
    const attributes: SohoAttributesArray = [ { name:  'data-automation-id',  value: 'lm-poll-and-print-modal' } ];
    this.pollDialog = this.lmModalWrapperService.modal(LMPollAndPrintDialogComponent, { fullsize: 'responsive', attributes });
    return this.pollDialog
      .title( this.lmFormatUtilService.formatMessage(this.component.printInfo.isExport ? Soho.Locale.translate('GeneratingCSV') : Soho.Locale.translate('GeneratingPDF'), this.lmSecurityUtilService.escape(name)))
      .buttons([
        {
          // Not able to change the button text on the fly to 'close', when the status changes to say, error
          // id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.RUNINBACKGROUND ]),
          // text: Soho.Locale.translate(LMDialogButtonKey.RUNINBACKGROUND),
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.DISMISS ]),
          text: Soho.Locale.translate(LMDialogButtonKey.DISMISS),
          cssClass: 'btn-cancel',
          click: () => this.pollDialog.close()
        },
      ])
      .open()
      .apply((pollDlg) => pollDlg.isExport = this.component.printInfo.isExport)
      .afterOpen(() => this.startPoll(asyncId))
      .beforeClose((pollDlg) => {
        if (this.lmContextService.isUnified()) {
          this.lmModalOpenService.removeModal(this.pollDialog.uuid);
        }
        // cancelPoll done in before close so Escape key will also stop polling.
        // Modal dialog implementation does not execute the 'Close' button handler on Escape.
        pollDlg.componentDialog.cancelPoll();
        return true;
      });
  }

  private startPoll(asyncId: string) {
    this.pollDialog.componentDialog.startPoll(asyncId);
  }

  // private toastCancellingMessage() {
  //   const url = this.lmUrlUtilService.buildUrl({ resource: this.printFilesResource });
  //   const toastOptions: SohoToastOptions = { allowLink: true, title: '', message:'' };
  //   // const message1 = Soho.Locale.translate('CancelPoll');
  //   const message = Soho.Locale.translate('PleaseVisitMyPrintFiles');
  //   toastOptions.message =
  //     `<a class="hyperlink" target="_blank" href="${url}">
  //         ${message}</a>`;
  //   this.sohoToastService.show(toastOptions);
  // }

  // public showPollDialogStatus(status: string, error?: LMError) {
    // if (status === AppConstants.STATUS_INPROGRESS) {
      // Aborting, since the document is taking a longer time to generate.
      // Close the dialog and show a toast.
      // this.pollDialog.close();
      // this.toastCancellingMessage();
    // } else if (status === AppConstants.STATUS_SYSTEM_ERROR) {
      // Close the dialog and Process unexpected error.
      // this.pollDialog.close();
      // this.lmErrorUtilService.handleError(error.message, error.details);
    // }
    // const dialogId = 'PollDialog';
    // this.pollDialog.buttons([
    //   {
    //     id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CLOSE ]),
    //     text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
    //     click: () => this.pollDialog.close()
    //   },
    // ]);
  // }

  public openPrintDocument(actionRedirect: LMActionRedirect) {
    this.pollDialog.close();
    const documentResource = new DocumentResource({
      resourceId: actionRedirect.resourceId,
      fieldName: actionRedirect.fieldName
    }, '');
    const url = this.lmUrlUtilService.buildUrl({ resource: documentResource });
    if (this.component.printInfo.isExport) {
      this.lmGlobalUtilService.navigate(url);
    } else {
      this.lmGlobalUtilService.navigate(url, null, 'external');
    }
  }
}

export type LMChartNames = { [key: string]: string };

export interface LMPrintDialogInfo {
  resource: Resource
  fromResource?: Resource
  fromList?: Resource
  actionReference: LMBusActionReference
  name: string
  chartNames?: LMChartNames;
  dataView?: LMDataView
  dataViewSet?: LMDataViewSet
  cubeOverrides?: LMCubeOverrides
  cubeOptions?: LMCubeOptions
  userFolder?: string
  selectedRows?: number[]
  filterFields?: LMFilterFieldCriteria[]
  scheduleActionAllowed?: boolean
  includeRecordLimit?: boolean
  isExport?: boolean
  selectRequestData?: SelectRequestData
  hasDetailSections?: boolean
  singleRecordSelected?: boolean
  retentionDuration?: number
  isArchiveData?: boolean
}

export interface LMPrintPdfInfo extends LMPrintDialogInfo {
  pdfType?: string
  fontOffset?: number
  orientation?: 'portrait' | 'landscape'
  paperSize?: string
  customWidth?: number
  customHeight?: number
  customUnit?: string
  selectedTabIndex?: number
  selectedPanelName?: string
  listParams?: LMPaneListParams
  includeSearchForm?: boolean
}

export interface LMPrintCsvInfo extends LMPrintDialogInfo {
  isTreeList?: boolean
  csvType?: string
  selectedDetailPanelIndex?: number
  detailList?: LMPanelDetail[]
  pageResourceId?: string;
  implementsAuditCompare?: boolean;
  showChangedValuesOnRight?: boolean;
  allowMultiThread?: boolean;
}

export interface LMPrintDialog {
  resource: Resource
  requestData: LMPrintRequestData
  requestParams: LMPrintRequestParams
  printInfo: LMPrintDialogInfo
  busyIndicator: SohoBusyIndicatorDirective
  checkIfFormIsValid(): boolean
  getSuccessMessage(scheduled: boolean, params?: LMPrintRequestParams): SohoToastOptions
  getErrorMessage(scheduled: boolean): SohoToastOptions
}

export interface LMPanelDetail {
  paneId?: string;
  name?: string
  title?: string | LMMessageRule
  parentPanel?: string
}
